import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();

  const formatApiError = (detail) => {
    if (!detail) return 'Login failed. Please try again.';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
    }
    return detail.msg || String(detail);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      toast.success('Welcome back!');
    } catch (err) {
      const errorMessage = formatApiError(err.response?.data?.detail);
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center relative"
      data-testid="login-page"
    >
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1696860682642-03203a617bce?w=1920&q=80)',
        }}
      />
      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-black/50" />

      {/* Login Card */}
      <div className="relative z-10 w-full max-w-md mx-4">
        <Card className="border-0 shadow-2xl bg-white/95 backdrop-blur-sm">
          <CardHeader className="text-center pb-2 pt-8">
            <div className="mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_edu-storage-core/artifacts/k2aah68p_ChatGPT%20Image%20Mar%207%2C%202026%20at%2002_55_17%20PM.png" 
                alt="GIHDS Logo" 
                className="h-20 w-auto mx-auto"
              />
            </div>
            <h1 className="text-2xl font-medium text-primary" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Geneva Institute of Humanitarian & Development Studies
            </h1>
            <p className="text-sm text-muted-foreground mt-2">
              Learning Portal
            </p>
          </CardHeader>
          <CardContent className="px-8 pb-8">
            <div className="mb-6">
              <h2 className="text-lg font-medium text-primary text-center" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Sign in to your account
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-sm text-destructive text-sm" data-testid="login-error">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your.email@gihds.org"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-11"
                  data-testid="login-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium">
                  Password
                </Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-11"
                  data-testid="login-password-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground rounded-sm"
                disabled={loading}
                data-testid="login-submit-button"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>

            <p className="text-center text-xs text-muted-foreground mt-6">
              Contact your administrator if you need access credentials
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
